import { useState } from 'react'

import { useForm } from 'react-hook-form'

import { useTranslation } from 'react-i18next'

import { Link, useNavigate } from 'react-router-dom'

import { zodResolver } from '@hookform/resolvers/zod'

import { useAuthStore } from '@/stores/auth'
import { useAppSettings } from '@/stores/useAppSettings'

import { paths } from '@/config/paths'

import { AuthFormFooter, AuthFormHeader } from '@/layouts/auth'
import { AuthLayout } from '@/layouts/auth/AuthLayout'

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'

import { EmailField } from '@/components/form-fields/EmailField'
import toast from '@/components/toast'
import { PasswordInput } from '@/components/ui-custom/PasswordInput'
import { SmartAlert } from '@/components/ui-custom/SmartAlert'
import { SmartButton } from '@/components/ui-custom/SmartButton'

import { loginSchema, type loginSchemaValues } from '@/schemas/auth/login.schema'

export function LoginPage() {
  const { t } = useTranslation(['auth'])
  const login = useAuthStore((state) => state.login)
  const navigate = useNavigate()
  const { getSetting } = useAppSettings()
  const [loading, setLoading] = useState(false)

  const isLoginDisabledForOthers = getSetting('security.disable_login_for_others', false)
  const isGymClosed = getSetting('operations.gym_closed', false)

  const form = useForm<loginSchemaValues>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  })

  const onSubmit = async (data: loginSchemaValues) => {
    setLoading(true)
    try {
      const user = await login(data.email, data.password)
      if (user?.roles) {
        toast.success(t('auth:login.success.welcome', 'Welcome back!'))
        navigate(paths.dashboard.root.getHref(), { replace: true })
      }
    } catch (error: any) {
      const message = error.message || t('auth:login.errors.loginFailed', 'Login failed')
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <AuthLayout>
        {/* Form */}
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className='flex w-full max-w-md flex-col gap-6 rounded-2xl bg-white p-6 shadow-md md:p-8'
            translate='no'
          >
            {isGymClosed && (
              <SmartAlert
                variant='destructive'
                title={t('auth:login.gymClosed.title', 'Gym Temporarily Closed')}
                description={t(
                  'auth:login.gymClosed.message',
                  'The gym is currently closed. Please check back later or contact support for more information.'
                )}
              />
            )}

            {isLoginDisabledForOthers && (
              <SmartAlert
                variant='warning'
                title={t('auth:login.loginRestricted.title', 'Login Restricted')}
                description={t(
                  'auth:login.loginRestricted.message',
                  'Login is currently restricted, users cannot log in at this time. Please contact support if yu have urgent questions'
                )}
              />
            )}

            <AuthFormHeader
              title={t('auth:login.form.title', 'Login to Account')}
              subtitle={t('auth:login.form.subtitle', 'Enter your email below to login to your account')}
            />

            {/* Email */}
            <EmailField
              form={form}
              name='email'
              label={t('auth:login.form.fields.email.label', 'Email')}
              placeholder={t('auth:login.form.fields.email.placeholder', 'Your Email')}
              tooltip={t('auth:login.form.fields.email.tooltip', 'Enter email that will be primary for your account')}
            />

            {/* Password Field */}
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <div className='flex items-center'>
                    <FormLabel>{t('auth:login.form.fields.password.label', 'Password')}</FormLabel>
                    <Link
                      to={paths.public.auth.resetPassword.getHref()}
                      className='ml-auto text-sm underline-offset-4 hover:underline'
                    >
                      {t('auth:loginForm.forgotPassword', 'Forgot your password?')}
                    </Link>
                  </div>
                  <FormControl>
                    <PasswordInput
                      placeholder={t('auth:loginForm.password.input.placeholder', 'Password')}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <SmartButton type='submit' className='w-full cursor-pointer' disabled={loading} loading={loading}>
              {t('auth:loginForm.submit', 'Login')}
            </SmartButton>

            {/* Register Link */}
            <AuthFormFooter
              text={t('auth:loginForm.noAccountMsg', "Don't have an account?")}
              links={[
                {
                  to: paths.public.auth.register.getHref(),
                  text: t('auth:loginForm.signUpLink', 'Sign up'),
                },
              ]}
            />
          </form>
        </Form>
      </AuthLayout>
    </>
  )
}
